// store/modalStore.ts
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export type ModalOptions = {
  title: string;
  confirmLabel: string;
  confirmButtonClassName?: string;
  cancelLabel?: string;
  cancelButtonClassName?: string;
  headerClassName?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  isLoading?: boolean;
};

export type Modal = ModalOptions & {
  id: string;
  isActive?: boolean;
  content: React.ReactNode;
};

interface ModalStore {
  modals: Modal[];
  openModal: (modal: Omit<Modal, "id">) => string;
  closeModal: () => void;
  closeAllModals: () => void;
}

export const useModalStore = create<ModalStore>()(
  subscribeWithSelector((set, get) => ({
    modals: [],
    openModal: (modal) => {
      const id = Date.now().toString();
      set((state) => ({
        modals: [...state.modals, { ...modal, id, isActive: true }],
      }));
      return id;
    },

    closeModal: () => {
      const firstPoppedModal = get().modals[0];
      set((state) => ({
        modals: [
          { ...firstPoppedModal, isActive: false },
          ...state.modals.slice(1),
        ],
      }));

      // Clean deletion after animation
      setTimeout(() => {
        set((state) => ({
          modals: state.modals.slice(1),
        }));
      }, 200);
    },

    closeAllModals: () => {
      set({ modals: [] });
    },
  })),
);

export const modal = (content: React.ReactNode, options: ModalOptions) => {
  return useModalStore.getState().openModal({
    content,
    ...options,
  });
};

export const closeModal = () => {
  return useModalStore.getState().closeModal();
};
